import { documentationClient } from 'shared/domain/documentation/documentationClient';
import type { StateCreator } from 'zustand';
import { isArchiveOperation } from 'shared/domain/documentation/operationTypeGuards';
import {
  DocumentationViewItem,
  isDirectoryItemType,
  isFileItemType,
} from 'store/documentation/model/types';
import { Identificable } from 'shared/types/commonView';
import {
  DocumentationOperationType,
} from 'shared/domain/documentation/operationPreflight';
import { OperationsSlice } from './types';
import type { DocumentationStoreSlices } from '../types';
import { INITIAL_OPERATIONS_STATE, collectItems } from './model';

export * from './types';

type OperationsSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  OperationsSlice
>;

export const createOperationsSlice: OperationsSliceCreator = (set, get) => ({
  ...INITIAL_OPERATIONS_STATE,

  selectItems: (
    items: DocumentationViewItem[],
    options?: { ctrlKeyPressed?: boolean }
  ): void => {
    const { selectedItems } = get();

    if (!options?.ctrlKeyPressed && items.length === 1) {
      const item = items[0];
      if (!selectedItems.has(item)) {
        set({ selectedItems: new Set(items) });
        return;
      }
    }

    const newSelection = new Set(selectedItems);
    items.forEach((item) => {
      if (newSelection.has(item) && options?.ctrlKeyPressed) {
        newSelection.delete(item);
      } else {
        newSelection.add(item);
      }
    });

    set({ selectedItems: newSelection });
  },

  unselectItems: (): void => {
    set({ selectedItems: new Set() });
  },

  setSelectedItems: (items: Set<DocumentationViewItem>): void => {
    set({ selectedItems: items });
  },

  moveSelectedToDirectory: (target: Identificable): void => {
    const { 
      selectedItems, 
      markRowsAsPending, 
      removeRowsOptimistically,
      markDirectoryAsNonEmpty,
    } = get();
    const id = target._id;

    let moveDirectoryToSelf = false;
    const documentations: number[] = [];
    const directories: number[] = [];
    const itemsToRemove: DocumentationViewItem[] = [];
    const idsToRemove: string[] = [];

    selectedItems.forEach((item) => {
      if (isDirectoryItemType(item.type)) {
        if (item._id === target._id) {
          moveDirectoryToSelf = true;
        }
        directories.push(item.localId);
      } else if (isFileItemType(item.type)) {
        documentations.push(item.localId);
      }

      if (item._id) {
        idsToRemove.push(item._id);
        itemsToRemove.push(item);
      }
    });

    if (moveDirectoryToSelf) {
      return;
    }

    // Optimistic update: mark as pending, then remove from view
    markRowsAsPending(idsToRemove);
    removeRowsOptimistically(idsToRemove, itemsToRemove);

    // Mark target directory as non-empty (updates folder icon immediately)
    if (id) {
      markDirectoryAsNonEmpty(id);
    }

    // Note: invalidateCache is called by the listener when backend confirms
    documentationClient.setParent(id, documentations, directories);
    set({ selectedItems: new Set() });
  },

  prepareSelectedOperation: async (
    operationType: DocumentationOperationType
  ) => {
    const { selectedItems } = get();
    const selectedByState = collectItems(
      selectedItems,
      isArchiveOperation(operationType)
        ? (item) => item.deleted === false
        : (item) => item.deleted === true
    );

    if (
      selectedByState.documentations.length === 0 &&
      selectedByState.directories.length === 0
    ) {
      set({ preparedOperation: null });
      return null;
    }

    return get().prepareOperationByIds(operationType, {
      documentations: selectedByState.documentations,
      directories: selectedByState.directories,
    });
  },

  prepareOperationByIds: async (
    operationType: DocumentationOperationType,
    params: { documentations: number[]; directories: number[] }
  ) => {
    if (params.documentations.length === 0 && params.directories.length === 0) {
      set({ preparedOperation: null });
      return null;
    }

    set({ isOperationInProgress: true });

    try {
      const preflightResult = await documentationClient.preflight({
        operationType,
        documentations: params.documentations,
        directories: params.directories,
      });

      const preparedOperation = {
        operationType,
        requestedDocumentations: params.documentations,
        requestedDirectories: params.directories,
        resolvedDocumentations: preflightResult.resolvedDocumentations,
        resolvedDirectories: preflightResult.resolvedDirectories,
        resolvedCount: preflightResult.resolvedCount,
        signature: preflightResult.signature,
        selectedItemsBreakdown: preflightResult.selectedItemsBreakdown,
      };

      set({
        preparedOperation,
        isOperationInProgress: false,
      });

      return preparedOperation;
    } catch {
      set({ isOperationInProgress: false });
      return null;
    }
  },

  confirmPreparedOperation: async () => {
    const { 
      preparedOperation, 
      selectedItems,
      markRowsAsPending, 
      removeRowsOptimistically,
      currentDirectory,
      fetchDirectoryContent,
    } = get();

    if (!preparedOperation) {
      return { status: 'missing' as const };
    }

    set({ isOperationInProgress: true });

    try {
      const refreshedPreflight = await documentationClient.preflight({
        operationType: preparedOperation.operationType,
        documentations: preparedOperation.requestedDocumentations,
        directories: preparedOperation.requestedDirectories,
      });

      if (refreshedPreflight.signature !== preparedOperation.signature) {
        set({
          preparedOperation: {
            ...preparedOperation,
            resolvedDocumentations: refreshedPreflight.resolvedDocumentations,
            resolvedDirectories: refreshedPreflight.resolvedDirectories,
            resolvedCount: refreshedPreflight.resolvedCount,
            signature: refreshedPreflight.signature,
            selectedItemsBreakdown: refreshedPreflight.selectedItemsBreakdown,
          },
          isOperationInProgress: false,
        });

        return {
          status: 'stale' as const,
          resolvedCount: refreshedPreflight.resolvedCount,
          resolvedDocumentationsCount:
            refreshedPreflight.resolvedDocumentations.length,
          resolvedDirectoriesCount:
            refreshedPreflight.resolvedDirectories.length,
        };
      }

      // Optimistic update for archive operations
      if (isArchiveOperation(preparedOperation.operationType)) {
        const itemsToRemove: DocumentationViewItem[] = [];
        const idsToRemove: string[] = [];

        // Bulk actions populate selectedItems, but gallery preview operations
        // may run without selection. In that case derive targets from prepared ids.
        selectedItems.forEach((item) => {
          if (item.deleted === false && item._id) {
            idsToRemove.push(item._id);
            itemsToRemove.push(item);
          }
        });

        if (idsToRemove.length === 0) {
          const requestedDocIds = new Set(preparedOperation.requestedDocumentations);
          const requestedDirIds = new Set(preparedOperation.requestedDirectories);

          get().filteredRows.forEach((item) => {
            if (item.deleted !== false || !item._id) {
              return;
            }

            if (
              (isFileItemType(item.type) && requestedDocIds.has(item.localId)) ||
              (isDirectoryItemType(item.type) && requestedDirIds.has(item.localId))
            ) {
              idsToRemove.push(item._id);
              itemsToRemove.push(item);
            }
          });
        }

        if (idsToRemove.length > 0) {
          markRowsAsPending(idsToRemove);
          removeRowsOptimistically(idsToRemove, itemsToRemove);
        }

        documentationClient.remove(
          preparedOperation.requestedDocumentations,
          preparedOperation.requestedDirectories,
          preparedOperation.signature
        );
      } else {
        // Restore needs full refresh
        fetchDirectoryContent(currentDirectory, { suppressLoadingIndicators: true });
        documentationClient.restore(
          preparedOperation.requestedDocumentations,
          preparedOperation.requestedDirectories,
          preparedOperation.signature
        );
      }

      set({
        selectedItems: new Set(),
        preparedOperation: null,
        isOperationInProgress: false,
      });

      return { status: 'executed' as const };
    } catch {
      set({ isOperationInProgress: false });
      return { status: 'missing' as const };
    }
  },

  clearPreparedOperation: (): void => {
    set({ preparedOperation: null });
  },

  setOperationInProgress: (inProgress: boolean): void => {
    set({ isOperationInProgress: inProgress });
  },

  getSelectedItemsByState: (): {
    all: { documentations: number[]; directories: number[] };
    onlyActive: { documentations: number[]; directories: number[] };
    onlyDeleted: { documentations: number[]; directories: number[] };
  } => {
    const { selectedItems } = get();

    return {
      all: collectItems(selectedItems),
      onlyActive: collectItems(selectedItems, (item) => item.deleted === false),
      onlyDeleted: collectItems(selectedItems, (item) => item.deleted === true),
    };
  },

  resetOperationsState: (): void => {
    set(INITIAL_OPERATIONS_STATE);
  },
});
